import type { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';

/**
 * Time options for dropdowns (15-minute intervals from 12:00 AM to 11:45 PM)
 * Used across appointments, email flows, and other time selection components
 */
export const TIME_OPTIONS: DropdownValue[] = Array.from({ length: 96 }, (_, i) => {
    const totalMinutes = i * 15;
    const hour = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour < 12 ? 'AM' : 'PM';
    const label = `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;

    return {
        label,
        value: String(totalMinutes),
    };
});

/**
 * Helper to convert minutes from midnight to HH:MM format (24-hour)
 * @param minutes - Minutes from midnight (0-1439)
 * @returns Time string in HH:MM format
 * @example minutesToTimeString(540) // "09:00"
 */
export function minutesToTimeString(minutes: number): string {
    const hour = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Helper to convert HH:MM time string to minutes from midnight
 * @param time - Time string in HH:MM format
 * @returns Minutes from midnight (0-1439)
 * @example timeStringToMinutes("09:00") // 540
 */
export function timeStringToMinutes(time: string): number {
    const [hour, min] = time.split(':').map(Number);
    return (hour || 0) * 60 + (min || 0);
}

/**
 * Format time from HH:MM (24-hour) to 12-hour format with AM/PM and date prefix
 * Used for appointment rescheduling API
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:MM format (24-hour)
 * @returns Formatted string like "2025-11-18 12:30 AM"
 *
 * @example
 * formatTimeFor12Hour('2025-11-18', '00:30') // "2025-11-18 12:30 AM"
 * formatTimeFor12Hour('2025-11-18', '09:00') // "2025-11-18 09:00 AM"
 * formatTimeFor12Hour('2025-11-18', '13:00') // "2025-11-18 01:00 PM"
 */
export function formatTimeFor12Hour(date: string, time: string): string {
    const parts = time.split(':').map(Number);
    const hour = parts[0] || 0;
    const minute = parts[1] || 0;
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour < 12 ? 'AM' : 'PM';
    return `${date} ${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`;
}

/**
 * Convert 24-hour time format to 12-hour format with AM/PM
 * @param time24 - Time string in HH:MM format (24-hour)
 * @returns Time string in 12-hour format with AM/PM (e.g., "9:00 AM", "5:30 PM")
 *
 * @example
 * formatTimeTo12Hour("09:00") // "9:00 AM"
 * formatTimeTo12Hour("17:30") // "5:30 PM"
 * formatTimeTo12Hour("00:00") // "12:00 AM"
 */
export function formatTimeTo12Hour(time24: string): string {
    const [hour, minute] = time24.split(':').map(Number);
    const hourValue = hour ?? 0;
    const minuteValue = minute ?? 0;
    const displayHour = hourValue === 0 ? 12 : hourValue > 12 ? hourValue - 12 : hourValue;
    const period = hourValue < 12 ? 'AM' : 'PM';
    return `${displayHour}:${minuteValue.toString().padStart(2, '0')} ${period}`;
}

/**
 * Parse API time format (e.g., "10:00 PM") to minutes from midnight
 * Handles the time format returned by availability API
 *
 * @param stime - Time string like "10:00" or "04:30"
 * @param ampm - Period indicator: "AM" or "PM"
 * @returns Minutes from midnight (0-1439), or -1 if parsing fails
 *
 * @example
 * parseApiTimeToMinutes("10:00", "PM") // 1320 (10:00 PM = 22:00)
 * parseApiTimeToMinutes("04:30", "AM") // 270 (4:30 AM)
 * parseApiTimeToMinutes("12:00", "AM") // 0 (midnight)
 * parseApiTimeToMinutes("12:00", "PM") // 720 (noon)
 */
export function parseApiTimeToMinutes(stime: string | undefined, ampm: string): number {
    if (!stime) return -1;

    const timeMatch = stime.match(/(\d{1,2}):(\d{2})/);
    if (!timeMatch || !timeMatch[1] || !timeMatch[2]) return -1;

    const hour = parseInt(timeMatch[1], 10);
    const minute = parseInt(timeMatch[2], 10);
    const isPM = ampm === 'PM';

    // Convert to 24-hour format
    let hour24: number;
    if (isPM && hour !== 12) {
        hour24 = hour + 12; // 1 PM = 13, 11 PM = 23
    } else if (!isPM && hour === 12) {
        hour24 = 0; // 12 AM = 0 (midnight)
    } else {
        hour24 = hour; // AM hours except 12, or 12 PM
    }

    return hour24 * 60 + minute;
}

/**
 * Get local date string in YYYY-MM-DD format
 * Unlike toISOString(), this returns the date in the user's local timezone
 *
 * @param date - Date object
 * @returns Date string in YYYY-MM-DD format in local timezone
 *
 * @example
 * // If it's Nov 18 11:00 PM EST (which is Nov 19 04:00 AM UTC)
 * getLocalDateString(date) // "2025-11-18" (local date, not UTC)
 */
export function getLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Parse a YYYY-MM-DD date string as a local date (not UTC)
 * Prevents timezone shift when creating Date objects from date strings
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing local midnight on that date
 *
 * @example
 * parseLocalDate("2025-11-19") // Nov 19, 2025 00:00:00 in local timezone (not UTC)
 */
export function parseLocalDate(dateString: string): Date {
    const [year = 0, month = 1, day = 1] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
}
